import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import Modal from 'components/UI/Modal';
import { renderToast, toast } from 'components/UI/ToastNotification';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { getBusinessUnitsForCompany } from 'requests/businessUnitCompany';
import { useAPI } from 'lib/API';
import initialColumns from 'constants/businessUnitRelationsColumns';
import { Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { CREDIT_SOURCES, CREDIT_STATUS_MESSAGES, BUSINESS_UNIT_RELATIONS } from 'constants/credit';
import ConnectionStatusBadge from 'components/Company/ConnectionStatusBadge';
import { applySortFilter, handleOnSortChange } from 'utils/grid';

const Span = styled.span`
  margin: ${Space.spBase} 0;
`;

interface Props {
  companyId: string;
  page?: string;
  updateBusinessUnit?: (id: number) => void;
};

const BusinessUnitRelationsTab = ({
  companyId,
  page,
  updateBusinessUnit
}: Props) => {
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [columns, setColumns] = useState(initialColumns);
  const [sortBy, setSortBy] = useState<any>(columns.find(c => c.defaultSort !== undefined));
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page) : 1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showModal, setShowModal] = useState(false);
  const [clickedBusinessUnitName, setClickedBusinessUnitName] = useState('');
  const [clickedBusinessUnitId, setClickedBusinessUnitId] = useState<number | null>(null);

  const calculatePages = (count: number, pageSize: number) => {
    const pagesCount = Math.ceil(count / pageSize);
    return (pagesCount === 0) ? 1 : pagesCount;
  };

  const applyPaginationFilter = (items: any[], pageIndex: number, pageSize: number) => {
    return items.slice((pageIndex - 1) * pageSize, pageIndex * pageSize);
  };

  const businessUnitsFromSort = applySortFilter(businessUnits, sortBy);
  const businessUnitsFromPagination = applyPaginationFilter(businessUnitsFromSort, currentPage, itemsPerPage);
  const pagesCount = calculatePages(businessUnits.length, itemsPerPage);

  const onSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const onSortChange = (index: number, direction: string) => handleOnSortChange(index, direction, columns, setSortBy);

  const businessUnitsLoadReq = useAPI({
    deferFn: getBusinessUnitsForCompany,
    onResolve: (result: any[]) => {
      setBusinessUnits(result);
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  useEffect(() => {
    if (companyId) {
      businessUnitsLoadReq.run(companyId);
    }
  }, [companyId]);

  const onRowClick = (event?: any, rowData?: any) => {
    setClickedBusinessUnitName(rowData.name);
    setClickedBusinessUnitId(rowData.id);
    setShowModal(true);
  };

  const onConfirmSwitchBusinessUnitClick = () => {
    if (updateBusinessUnit && clickedBusinessUnitId) {
      updateBusinessUnit(clickedBusinessUnitId);
    }
  };

  const mappedColumns = columns.map((column: any) => {
    if (column.field === 'businessUnitCompanyId') {
      column.render = (params: any) => (
        <span style={{ color: params.businessUnitCompanyId === BUSINESS_UNIT_RELATIONS.NOT_ASSIGNED ? Main.error : Text.primary }}>
          {params.creditSourceSystem === CREDIT_SOURCES.INTERNAL ?
            <ConnectionStatusBadge
              status={CREDIT_STATUS_MESSAGES.TRACKED_BY_OFMP}
              backgroundColor={Main.success}
              color={Text.whitePrimary}
              margin='0px'
              width='160px'
            />
            : params.businessUnitCompanyId
          }
        </span>
      );
    }

    return column;
  });

  return (
    <>
      <GenericTableWrapper
        columns={mappedColumns}
        data={businessUnitsFromPagination}
        isLoading={businessUnitsLoadReq.isLoading}
        onRowClick={onRowClick}
        toolbar
        overrideToolbar
        onOrderChange={onSortChange}
        sortBy={sortBy}
        customPagination={{
          itemsPerPage,
          setItemsPerPage: onSizeChange,
          currentPage,
          setCurrentPage,
          totalPages: pagesCount,
          baseRoute: '/companies',
          shouldRedirect: false
        }}
      />
      <Modal
        title={BUSINESS_UNIT_RELATIONS.MODAL_TITLE}
        onClose={() => setShowModal(false)}
        showModal={showModal}
        theme="accent"
        onClick={onConfirmSwitchBusinessUnitClick}
        primaryButtonText={BUSINESS_UNIT_RELATIONS.MODAL_BUTTON_TEXT}
      >
        <FlexboxContainer flexDirection="column">
          <Span>
            {BUSINESS_UNIT_RELATIONS.MODAL_MESSAGE_PREFIX} {clickedBusinessUnitName} {BUSINESS_UNIT_RELATIONS.MODAL_MESSAGE_SUFFIX}
          </Span>
        </FlexboxContainer>
      </Modal>
    </>
  );
};

export default BusinessUnitRelationsTab;
